import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Space } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Booking, BookingStatus } from '@/models';
import { isButtonDisabled, showMessage } from '@/utils';
import { useSendBookingSummary } from '@/web-api/bookings';

type SendSummaryButtonProps = {
  isEditingMode: boolean;
  data: Booking;
};

export function SendSummaryButton({ isEditingMode, data }: SendSummaryButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm<{ emails: string[] }>();
  const { t } = useTranslation();
  const sendSummaryMutation = useSendBookingSummary();

  function showModal() {
    setIsModalOpen(true);
    form.resetFields();
    // Initialize with one empty email field
    form.setFieldsValue({ emails: [''] });
  }

  function handleCancel() {
    setIsModalOpen(false);
    form.resetFields();
  }

  const onSendSummary = async () => {
    try {
      const values = await form.validateFields();
      const emails = values.emails.filter((email: string) => email && email.trim());

      if (data?.id) {
        sendSummaryMutation.mutate(
          { bookingId: data.id, emails },
          {
            onSuccess: () => {
              showMessage('success', t('common:summarySent'));
              setIsModalOpen(false);
              form.resetFields();
            },
          }
        );
      }
    } catch (error) {
      // Form validation error
      console.error('Form validation error:', error);
    }
  };

  const isDisabled = isEditingMode || isButtonDisabled(data.status, [BookingStatus.VERIFIED]);

  return (
    <>
      <Button
        aria-label={t('common:sendSummary') || 'Send booking summary'}
        disabled={isDisabled}
        onClick={showModal}
      >
        {t('common:sendSummary') || 'Send Summary'}
      </Button>
      <Modal
        title={t('common:sendSummary') || 'Send Booking Summary'}
        open={isModalOpen}
        okText={t('common:confirm') || 'Send'}
        onOk={onSendSummary}
        onCancel={handleCancel}
        closable={true}
        confirmLoading={sendSummaryMutation.isPending}
      >
        <p>
          {t('common:sendSummaryText') || 'Enter email addresses to send the booking summary to:'}
        </p>
        <Form form={form} layout='vertical'>
          <Form.List name='emails'>
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => (
                  <Form.Item label={t('forms:email') || `Email ${index + 1}`} key={field.key}>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item
                        {...field}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            type: 'email',
                            message: t('forms:emailInvalid') || 'Invalid email address',
                          },
                        ]}
                        noStyle
                      >
                        <Input aria-label={`Email ${index + 1}`} placeholder='email@example.com' />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button
                          danger
                          icon={<MinusOutlined />}
                          onClick={() => remove(field.name)}
                          style={{ width: '40px' }}
                        />
                      )}
                    </Space.Compact>
                  </Form.Item>
                ))}
                <Form.Item>
                  <Button type='dashed' onClick={() => add()} icon={<PlusOutlined />} block>
                    {t('common:add') || 'Add Email'}
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </>
  );
}
